import {getTestProps} from '@benchling/js-test-utils';
import {Button, Checkbox, Group, Stack, Text} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import ModalFooter from 'ui-library/modals/ModalFooter';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import ReorderableSimpleTableRow, {
  ReorderableSimpleTableRowProps,
} from 'ui-library/reorderable/ReorderableSimpleTableRow';
import runModal, {RunModalProps} from 'ui-library/runModal';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';

/** The order and visibility the user chose for the reorderable columns. */
export interface CustomizeColumnsResult {
  /** The reorderable columns' `accessorKey`s in the chosen display order. */
  orderedKeys: Array<string>;
  /**
   * The full set of hidden column `accessorKey`s. Preserves any incoming hidden keys that are not
   * among the reorderable columns (e.g. columns absent from the current listing).
   */
  hiddenColumnKeys: Array<string>;
}

export interface CustomizeColumnsModalProps extends RunModalProps<CustomizeColumnsResult> {
  /**
   * The columns the user may reorder and show/hide, in their current display order. The modal
   * resolves with the new order and the updated hidden-column set.
   */
  reorderableColumns: Array<ScrollableTableColumn>;
  /** The currently hidden column `accessorKey`s (may include keys outside `reorderableColumns`). */
  hiddenColumnKeys: Array<string>;
  /**
   * Whether the modal offers show/hide controls. When `false`, the modal is reorder-only: no
   * visibility checkboxes or "Show all columns" toggle are shown and `hiddenColumnKeys` is returned
   * unchanged. Defaults to `true`.
   */
  canFilterColumns?: boolean;
}

/**
 * Opens {@link CustomizeColumnsModal} via `runModal` and resolves with the new column order and
 * visibility, or `undefined` if the modal is dismissed without saving.
 */
export function openCustomizeColumnsModal(
  reorderableColumns: Array<ScrollableTableColumn>,
  hiddenColumnKeys: Array<string>,
  canFilterColumns: boolean = true
): Promise<CustomizeColumnsResult | undefined> {
  return runModal(
    CustomizeColumnsModal,
    {reorderableColumns, hiddenColumnKeys, canFilterColumns},
    {title: 'Customize columns'}
  );
}

export function CustomizeColumnsModal({
  reorderableColumns,
  hiddenColumnKeys,
  canFilterColumns = true,
  close,
}: CustomizeColumnsModalProps): JSX.Element {
  const [orderedKeys, setOrderedKeys] = useState<Array<string>>(() =>
    reorderableColumns.map((column) => column.accessorKey)
  );
  const [hiddenKeys, setHiddenKeys] = useState<ReadonlySet<string>>(() => new Set(hiddenColumnKeys));

  const headerByKey = useMemo(
    () => new Map(reorderableColumns.map((column) => [column.accessorKey, column.header])),
    [reorderableColumns]
  );
  // Columns with `disableFiltering` can be reordered but not shown/hidden, so they get no checkbox
  // and are excluded from the show/hide-all toggle. When `canFilterColumns` is false the modal is
  // reorder-only, so no column is filterable.
  const filterableKeys = useMemo(
    () =>
      new Set(
        canFilterColumns
          ? reorderableColumns
              .filter((column) => !column.disableFiltering)
              .map((column) => column.accessorKey)
          : []
      ),
    [canFilterColumns, reorderableColumns]
  );

  const areAllVisible = [...filterableKeys].every((key) => !hiddenKeys.has(key));
  const areAnyVisible = [...filterableKeys].some((key) => !hiddenKeys.has(key));

  const toggleColumn = (accessorKey: string): void => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(accessorKey)) {
        next.delete(accessorKey);
      } else {
        next.add(accessorKey);
      }
      return next;
    });
  };

  const toggleAll = (): void => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (areAllVisible) {
        filterableKeys.forEach((key) => next.add(key));
      } else {
        filterableKeys.forEach((key) => next.delete(key));
      }
      return next;
    });
  };

  const getListItem = (accessorKey: string): ItemData<ReorderableSimpleTableRowProps> => {
    const label = headerByKey.get(accessorKey) ?? accessorKey;
    return {
      id: accessorKey,
      itemClass: ReorderableSimpleTableRow,
      itemProps: {
        isDraggingDisabled: false,
        row: {
          id: accessorKey,
          cells: [
            {
              key: accessorKey,
              contents: filterableKeys.has(accessorKey) ? (
                <Checkbox
                  checked={!hiddenKeys.has(accessorKey)}
                  label={label}
                  onChange={() => {
                    toggleColumn(accessorKey);
                  }}
                  {...getTestProps({component: 'CustomizeColumnsModal', element: 'columnToggle'})}
                />
              ) : (
                <Text>{label}</Text>
              ),
            },
          ],
        },
      },
    };
  };

  return (
    <Stack spacing="md" {...getTestProps({component: 'CustomizeColumnsModal', element: 'root'})}>
      <Text>
        {canFilterColumns
          ? 'Check columns to show them, and drag to change their order in the results table.'
          : 'Drag to change the order of columns in the results table.'}
      </Text>
      {canFilterColumns && (
        <Checkbox
          checked={areAllVisible}
          indeterminate={!areAllVisible && areAnyVisible}
          label="Show all columns"
          onChange={toggleAll}
          {...getTestProps({component: 'CustomizeColumnsModal', element: 'toggleAll'})}
        />
      )}
      <ReorderableList
        itemDatas={orderedKeys.map((accessorKey) => getListItem(accessorKey))}
        onMoveItem={setOrderedKeys}
        onMoveItemEnd={() => {}}
      />
      <ModalFooter>
        <Group position="right">
          <Button variant="secondary" onClick={() => close()}>
            Cancel
          </Button>
          <Button
            onClick={() => close({orderedKeys, hiddenColumnKeys: [...hiddenKeys]})}
            {...getTestProps({component: 'CustomizeColumnsModal', element: 'save'})}
          >
            Save
          </Button>
        </Group>
      </ModalFooter>
    </Stack>
  );
}

export default CustomizeColumnsModal;
